import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { institutionAPI } from '../../lib/api';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  CreditCard, 
  Users, 
  Clock,
  CheckCircle,
  AlertTriangle,
  Edit2,
  Save,
  X,
  Download,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import UsageLimitsCard from '../../components/Usage/UsageLimitsCard';
import FeatureAccessCard from '../../components/Usage/FeatureAccessCard';
import api from '../../lib/api';
import { apiErrorMessage } from '../../lib/utils';

export default function InstitutionSettings() {
  const { user, institution: authInstitution, logout } = useAuth();
  const navigate = useNavigate();
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const loadInstitution = async () => {
      if (!user?.institution_id) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await institutionAPI.getById(user.institution_id);
        setInstitution(response.data);
        setEditData({
          institution_name: response.data.institution_name || '',
          contact_email: response.data.contact_email || '',
          contact_phone: response.data.contact_phone || '',
          address: response.data.address || '',
        });
      } catch (error) {
        toast.error('Failed to load institution details');
      } finally {
        setLoading(false);
      }
    };
    
    loadInstitution();
  }, [user?.institution_id]);

  const fetchInstitution = async () => {
    if (!user?.institution_id) return;
    
    try {
      const response = await institutionAPI.getById(user.institution_id);
      setInstitution(response.data);
      setEditData({
        institution_name: response.data.institution_name || '',
        contact_email: response.data.contact_email || '',
        contact_phone: response.data.contact_phone || '',
        address: response.data.address || '',
      });
    } catch (error) {
      toast.error('Failed to load institution details');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await institutionAPI.update(user.institution_id, editData);
      setInstitution({ ...institution, ...editData });
      setEditing(false);
      toast.success('Institution updated successfully');
    } catch (error) {
      toast.error('Failed to update institution');
    } finally {
      setSaving(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await api.get('/institution/export-data-summary');
      const data = response.data;
      
      // Create a downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${institution.institution_code}_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to export data'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmName !== institution?.institution_name) {
      toast.error('Institution name does not match');
      return;
    }
    
    setDeleting(true);
    try {
      await api.post('/institution/self-delete', {
        institution_name: deleteConfirmName,
        reason: 'User requested deletion'
      });
      
      toast.success('Institution deleted successfully. Logging out...');
      setTimeout(() => {
        logout().finally(() => { window.location.href = '/login'; });
      }, 2000);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to delete institution'));
      setDeleting(false);
    }
  };


  const getStatusBadge = (status) => {
    switch (status) {
      case 'trial':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
            <Clock className="w-4 h-4" />
            Trial
          </span>
        );
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-4 h-4" />
            Active
          </span>
        );
      case 'expired':
      case 'suspended':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
            <AlertTriangle className="w-4 h-4" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        );
      default:
        return null;
    }
  };

  const getDaysRemaining = () => {
    if (!institution?.trial_end_date) return null;
    const endDate = new Date(institution.trial_end_date);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/4"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!user?.institution_id) {
    return (
      <div className="p-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Platform Administrator</h2>
            <p className="text-slate-500">
              You are logged in as a platform administrator. 
              Institution settings are only available for tenant users.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="p-8">
        <Card className="max-w-2xl mx-auto">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-16 h-16 mx-auto text-amber-400 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Institution Not Found</h2>
            <p className="text-slate-500">
              Unable to load institution details. Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining();

  return (
    <div className="p-8 space-y-6" data-testid="institution-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Institution Settings</h1>
          <p className="text-slate-500 mt-1">Manage your institution&apos;s information and subscription</p>
        </div>
        {user?.is_master_admin && !editing && (
          <Button 
            onClick={() => setEditing(true)}
            variant="outline"
            className="gap-2"
            data-testid="edit-institution-btn"
          >
            <Edit2 className="w-4 h-4" />
            Edit Details
          </Button>
        )}
      </div>

      {/* Subscription Status Card */}
      <Card className="border-l-4 border-l-cyan-500">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Subscription Status</CardTitle>
              <CardDescription>Your current plan and trial information</CardDescription>
            </div>
            {getStatusBadge(institution.subscription_status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-1">Plan Type</p>
              <p className="text-lg font-semibold text-slate-900 capitalize">
                {institution.subscription_status === 'trial' ? 'Free Trial' : institution.plan_type || 'Standard'}
              </p>
            </div>
            
            {institution.subscription_status === 'trial' && daysRemaining !== null && (
              <div className={`rounded-lg p-4 ${daysRemaining <= 3 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                <p className="text-sm text-slate-500 mb-1">Trial Ends</p>
                <p className={`text-lg font-semibold ${daysRemaining <= 3 ? 'text-amber-600' : 'text-slate-900'}`}>
                  {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days remaining`}
                </p>
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500 mb-1">Institution Code</p>
              <p className="text-lg font-mono font-semibold text-slate-900">
                {institution.institution_code}
              </p>
            </div>
          </div>

          {institution.subscription_status === 'trial' && (
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-1">Ready to Subscribe?</h3>
                  <p className="text-cyan-100 text-sm">
                    Unlock unlimited access and premium features for your institution.
                  </p>
                </div>
                <Button 
                  className="bg-white text-cyan-600 hover:bg-cyan-50"
                  data-testid="subscribe-now-btn"
                  onClick={() => navigate('/subscription')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscribe Now
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Usage & Features Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UsageLimitsCard />
        <FeatureAccessCard />
      </div>

      {/* Institution Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Institution Details</CardTitle>
          <CardDescription>Basic information about your institution</CardDescription>
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="institution_name">Institution Name</Label>
                  <Input
                    id="institution_name"
                    value={editData.institution_name}
                    onChange={(e) => setEditData({ ...editData, institution_name: e.target.value })}
                    data-testid="edit-institution-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={editData.contact_email}
                    onChange={(e) => setEditData({ ...editData, contact_email: e.target.value })}
                    data-testid="edit-contact-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={editData.contact_phone}
                    onChange={(e) => setEditData({ ...editData, contact_phone: e.target.value })}
                    data-testid="edit-contact-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={editData.address}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    data-testid="edit-address"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  data-testid="save-institution-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setEditing(false)}
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <Building2 className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Institution Name</p>
                  <p className="text-slate-900 font-medium">{institution.institution_name}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Contact Email</p>
                  <p className="text-slate-900 font-medium">{institution.contact_email || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Contact Phone</p>
                  <p className="text-slate-900 font-medium">{institution.contact_phone || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p className="text-slate-900 font-medium">{institution.address || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Registered On</p>
                  <p className="text-slate-900 font-medium">
                    {institution.created_at ? new Date(institution.created_at).toLocaleDateString() : '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-slate-500">Contact Person</p>
                  <p className="text-slate-900 font-medium">{institution.contact_person_name || '-'}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Danger Zone - Only for Master Admin */}
      {user?.is_master_admin && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription>
              Irreversible actions for your institution account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Export Data */}
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex-1">
                <p className="font-medium text-slate-900 mb-1">Export All Data</p>
                <p className="text-sm text-slate-600">
                  Download a complete summary of all your records before deletion.
                  Includes patients, prescriptions, bills, and financial data.
                </p>
                <p className="text-xs text-slate-500 mt-2">
                  📋 Medical records retained for 3 years (10 years for medico-legal cases)
                </p>
              </div>
              <Button 
                variant="outline"
                onClick={handleExportData}
                disabled={exporting}
                className="ml-4 border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export Data'}
              </Button>
            </div>
            
            {/* Delete Account */}
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex-1">
                <p className="font-medium text-slate-900 mb-1">Delete Institution Account</p>
                <p className="text-sm text-slate-600">
                  Permanently delete your institution and disable all user access.
                  Medical records will be retained as per legal requirements.
                </p>
                <p className="text-xs text-amber-600 mt-2 font-medium">
                  ⚠️ This action cannot be undone. Export your data first!
                </p>
              </div>
              <Button 
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="ml-4"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Institution Account</DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">
                ⚠️ Before you proceed:
              </p>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>All users will be deactivated immediately</li>
                <li>Access to the system will be disabled</li>
                <li>Medical records retained for 3-10 years (legal requirement)</li>
                <li>Make sure you&apos;ve exported your data</li>
              </ul>
            </div>
            
            <div>
              <Label htmlFor="confirm-name" className="text-slate-900 font-medium">
                Type your institution name to confirm:
              </Label>
              <p className="text-sm text-slate-600 mb-2">
                <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">
                  {institution?.institution_name}
                </span>
              </p>
              <Input
                id="confirm-name"
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder="Type institution name exactly"
                className="font-mono"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeleteConfirmName('');
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmName !== institution?.institution_name}
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
